import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import toast from 'react-hot-toast';
import Button from '../components/ui/Button';
import { icons } from '../components/ui/Icons';

// Schemas de validação
const contactSchema = z.object({
  name: z.string().min(3, 'Nome deve ter pelo menos 3 caracteres'),
  email: z.string().email('Email inválido'),
  message: z.string().min(10, 'Mensagem deve ter pelo menos 10 caracteres'),
});

const barbershopRequestSchema = z.object({
  ownerName: z.string().min(3, 'Nome deve ter pelo menos 3 caracteres'),
  barbershopName: z.string().min(3, 'Nome da barbearia deve ter pelo menos 3 caracteres'),
  phone: z.string().min(10, 'Telefone inválido'),
  city: z.string().min(3, 'Cidade deve ter pelo menos 3 caracteres'),
  message: z.string().optional(),
});

const ContactPage = () => {
  const [activeTab, setActiveTab] = useState('contact'); // 'contact' ou 'barbershop'
  const [isSubmittingContact, setIsSubmittingContact] = useState(false);
  const [isSubmittingBarbershop, setIsSubmittingBarbershop] = useState(false);

  // Form para contato geral
  const {
    register: registerContact,
    handleSubmit: handleSubmitContact,
    formState: { errors: errorsContact },
    reset: resetContact,
  } = useForm({
    resolver: zodResolver(contactSchema),
  });

  // Form para solicitação de barbearia
  const {
    register: registerBarbershop,
    handleSubmit: handleSubmitBarbershop,
    formState: { errors: errorsBarbershop },
    reset: resetBarbershop,
  } = useForm({
    resolver: zodResolver(barbershopRequestSchema),
  });

  // Configuração da API
  const API_URL = process.env.REACT_APP_BACKEND_URL || '';

  const onSubmitContact = async (data) => {
    setIsSubmittingContact(true);

    try {
      const response = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          type: 'contact',
          data: {
            name: data.name,
            email: data.email,
            message: data.message,
          }
        }),
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.error || 'Erro ao enviar mensagem');
      }

      toast.success('Mensagem enviada com sucesso! Responderemos em breve.');
      resetContact();
    } catch (error) {
      console.error('Erro ao enviar mensagem:', error);
      toast.error(error.message || 'Erro ao enviar mensagem. Tente novamente mais tarde.');
    } finally {
      setIsSubmittingContact(false);
    }
  };

  const onSubmitBarbershop = async (data) => {
    setIsSubmittingBarbershop(true);

    try {
      const response = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          type: 'barbershop',
          data: {
            ownerName: data.ownerName,
            barbershopName: data.barbershopName,
            phone: data.phone,
            city: data.city,
            message: data.message,
          }
        }),
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.error || 'Erro ao enviar solicitação');
      }

      toast.success('Solicitação enviada com sucesso! Entraremos em contato em breve.');
      resetBarbershop();
    } catch (error) {
      console.error('Erro ao enviar solicitação:', error);
      toast.error(error.message || 'Erro ao enviar solicitação. Tente novamente mais tarde.');
    } finally {
      setIsSubmittingBarbershop(false);
    }
  };

  return (
    <div className="bg-gray-50 min-h-screen py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900">Entre em Contato</h1>
          <p className="mt-2 text-gray-600">
            Estamos aqui para ajudar você. Envie sua mensagem ou solicite o cadastro da sua barbearia.
          </p>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="flex border-b border-gray-200">
            <button
              onClick={() => setActiveTab('contact')}
              className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
                activeTab === 'contact'
                  ? 'bg-indigo-50 text-indigo-700 border-b-2 border-indigo-600'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
              }`}
            >
              <div className="flex items-center justify-center gap-2">
                {icons.mail({ className: 'w-4 h-4' })}
                <span>Contato Geral</span>
              </div>
            </button>
            <button
              onClick={() => setActiveTab('barbershop')}
              className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
                activeTab === 'barbershop'
                  ? 'bg-indigo-50 text-indigo-700 border-b-2 border-indigo-600'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
              }`}
            >
              <div className="flex items-center justify-center gap-2">
                {icons.building({ className: 'w-4 h-4' })}
                <span>Cadastrar Barbearia</span>
              </div>
            </button>
          </div>

          <div className="p-6">
            {/* Formulário de Contato Geral */}
            {activeTab === 'contact' && (
              <form onSubmit={handleSubmitContact(onSubmitContact)} className="space-y-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                    Nome *
                  </label>
                  <input
                    type="text"
                    id="name"
                    {...registerContact('name')}
                    className={`mt-1 block w-full border rounded-lg shadow-sm p-2 focus:ring-indigo-500 focus:border-indigo-500 ${
                      errorsContact.name ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Seu nome completo"
                  />
                  {errorsContact.name && (
                    <p className="mt-1 text-sm text-red-600">{errorsContact.name.message}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                    Email *
                  </label>
                  <input
                    type="email"
                    id="email"
                    {...registerContact('email')}
                    className={`mt-1 block w-full border rounded-lg shadow-sm p-2 focus:ring-indigo-500 focus:border-indigo-500 ${
                      errorsContact.email ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="seu.email@exemplo.com"
                  />
                  {errorsContact.email && (
                    <p className="mt-1 text-sm text-red-600">{errorsContact.email.message}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700">
                    Mensagem *
                  </label>
                  <textarea
                    id="message"
                    {...registerContact('message')}
                    rows={5}
                    className={`mt-1 block w-full border rounded-lg shadow-sm p-2 focus:ring-indigo-500 focus:border-indigo-500 ${
                      errorsContact.message ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Como podemos ajudar você?"
                  />
                  {errorsContact.message && (
                    <p className="mt-1 text-sm text-red-600">{errorsContact.message.message}</p>
                  )}
                </div>

                <Button
                  type="submit"
                  disabled={isSubmittingContact}
                  className="w-full flex items-center justify-center gap-2"
                >
                  {isSubmittingContact ? (
                    'Enviando...'
                  ) : (
                    <>
                      {icons.send({ className: 'w-4 h-4' })}
                      <span>Enviar Mensagem</span>
                    </>
                  )}
                </Button>
              </form>
            )}

            {/* Formulário de Solicitação de Cadastro de Barbearia */}
            {activeTab === 'barbershop' && (
              <form onSubmit={handleSubmitBarbershop(onSubmitBarbershop)} className="space-y-4">
                <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4 mb-4">
                  <p className="text-sm text-indigo-800">
                    <strong>Quer cadastrar sua barbearia?</strong> Preencha o formulário abaixo e
                    entraremos em contato para liberar o acesso.
                  </p>
                </div>

                <div>
                  <label htmlFor="ownerName" className="block text-sm font-medium text-gray-700">
                    Seu Nome *
                  </label>
                  <input
                    type="text"
                    id="ownerName"
                    {...registerBarbershop('ownerName')}
                    className={`mt-1 block w-full border rounded-lg shadow-sm p-2 focus:ring-indigo-500 focus:border-indigo-500 ${
                      errorsBarbershop.ownerName ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Nome do proprietário"
                  />
                  {errorsBarbershop.ownerName && (
                    <p className="mt-1 text-sm text-red-600">{errorsBarbershop.ownerName.message}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="barbershopName" className="block text-sm font-medium text-gray-700">
                    Nome da Barbearia *
                  </label>
                  <input
                    type="text"
                    id="barbershopName"
                    {...registerBarbershop('barbershopName')}
                    className={`mt-1 block w-full border rounded-lg shadow-sm p-2 focus:ring-indigo-500 focus:border-indigo-500 ${
                      errorsBarbershop.barbershopName ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Nome da sua barbearia"
                  />
                  {errorsBarbershop.barbershopName && (
                    <p className="mt-1 text-sm text-red-600">
                      {errorsBarbershop.barbershopName.message}
                    </p>
                  )}
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
                    Telefone/WhatsApp *
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    {...registerBarbershop('phone')}
                    className={`mt-1 block w-full border rounded-lg shadow-sm p-2 focus:ring-indigo-500 focus:border-indigo-500 ${
                      errorsBarbershop.phone ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="(11) 98765-4321"
                  />
                  {errorsBarbershop.phone && (
                    <p className="mt-1 text-sm text-red-600">{errorsBarbershop.phone.message}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="city" className="block text-sm font-medium text-gray-700">
                    Cidade *
                  </label>
                  <input
                    type="text"
                    id="city"
                    {...registerBarbershop('city')}
                    className={`mt-1 block w-full border rounded-lg shadow-sm p-2 focus:ring-indigo-500 focus:border-indigo-500 ${
                      errorsBarbershop.city ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="São Paulo"
                  />
                  {errorsBarbershop.city && (
                    <p className="mt-1 text-sm text-red-600">{errorsBarbershop.city.message}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="barbershopMessage" className="block text-sm font-medium text-gray-700">
                    Observações (opcional)
                  </label>
                  <textarea
                    id="barbershopMessage"
                    {...registerBarbershop('message')}
                    rows={3}
                    className="mt-1 block w-full border border-gray-300 rounded-lg shadow-sm p-2 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="Informações adicionais sobre sua barbearia"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={isSubmittingBarbershop}
                  className="w-full flex items-center justify-center gap-2"
                >
                  {isSubmittingBarbershop ? (
                    'Enviando...'
                  ) : (
                    <>
                      {icons.send({ className: 'w-4 h-4' })}
                      <span>Enviar Solicitação</span>
                    </>
                  )}
                </Button>
              </form>
            )}
          </div>
        </div>

        {/* Informações de Contato Adicionais */}
        <div className="mt-8 text-center">
          <p className="text-gray-600">
            Ou envie um email diretamente para:{' '}
            <a
              href="mailto:barberizeapp01@gmail.com"
              className="text-indigo-600 hover:text-indigo-800 font-medium"
            >
              barberizeapp01@gmail.com
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;
